import type { CompanySiteType } from '@api';
import { AddressFields, ContactFields, FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  UpdateCompanySiteFormValuesType,
  useUpdateCompanySite,
  useUpdateCompanySiteFormSchema,
} from './hooks';

type UpdateCompanySiteFormPropsType = {
  companyId: string;
  site: CompanySiteType;
  onClose: () => void;
};

export const UpdateCompanySiteForm: React.FC<
  UpdateCompanySiteFormPropsType
> = ({ companyId, site, onClose }) => {
  const { t } = useTranslation('companyDetailsGeneral');
  const { updateCompanySite } = useUpdateCompanySite(companyId, site);
  const { schema, defaultValues } = useUpdateCompanySiteFormSchema(site);

  const methods = useForm<UpdateCompanySiteFormValuesType>({
    resolver: zodResolver(schema),
    defaultValues,
    values: defaultValues,
  });

  const handleSubmit = async (data: UpdateCompanySiteFormValuesType) => {
    await updateCompanySite(site.id, data);
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleSubmit)}
        data-testid="update-company-site-form"
      >
        <Stack gap="xl">
          <AddressFields.Root>
            <AddressFields.Street />
            <AddressFields.Addition />
            <AddressFields.Zip />
            <AddressFields.City />
            <AddressFields.Country />
          </AddressFields.Root>
          <ContactFields.Root>
            <ContactFields.Phone />
            <ContactFields.Email />
            <ContactFields.Fax />
          </ContactFields.Root>
          <FormActions
            onCancel={onClose}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
